import clsx from 'clsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ActionItem } from '@/api/types';
import { describeAction, groupTitle, type ActionGroup } from '@/lib/actions';
import { FeedbackDialog } from './FeedbackDialog';

const order: ActionGroup[] = ['decide', 'give', 'pay', 'sign'];

/** What the client owes the work, grouped by the kind of effort: decide, give, pay, sign. */
export function ActionList({ actions, projectTitle, columns = 2 }: { actions: ActionItem[]; projectTitle?: (id?: string) => string | undefined; columns?: 1 | 2 }) {
  const [feedback, setFeedback] = useState<{ projectId: string; context: string }>();
  const views = actions.map((a) => ({ a, v: describeAction(a) }));
  const groups = order.map((g) => ({ g, items: views.filter((x) => x.v.group === g) })).filter((x) => x.items.length);

  return (
    <>
      <div className={clsx('grid grid-cols-1 gap-x-10 gap-y-8', columns === 2 && 'lg:grid-cols-2')}>
        {groups.map(({ g, items }) => (
          <section key={g} className="min-w-0">
            <h3 className="mb-1 flex items-baseline gap-2 text-sm font-semibold">
              {groupTitle[g]}
              <span className="num font-normal text-faint">{items.length}</span>
            </h3>
            <ul className="divide-y divide-line border-y border-line">
              {items.map(({ a, v }) => {
                const project = projectTitle?.(v.projectId);
                const body = (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{v.title}</span>
                      <span className="block truncate text-xs text-muted">
                        {v.hint}
                        {project && <span className="text-faint">, {project}</span>}
                      </span>
                    </span>
                    {v.due && <span className="hidden shrink-0 text-xs text-waiting sm:block">{v.due}</span>}
                    <span className="btn-secondary h-8 shrink-0 px-2.5 text-xs sm:h-7">{v.cta}</span>
                  </>
                );
                return (
                  <li key={v.key}>
                    {a.kind === 'feedback' ? (
                      <button onClick={() => setFeedback({ projectId: a.projectId, context: a.context })} className="flex w-full items-center gap-3 py-2.5 text-left hover:bg-panel">
                        {body}
                      </button>
                    ) : (
                      <Link to={v.to} className="flex items-center gap-3 py-2.5 hover:bg-panel">
                        {body}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
      {feedback && <FeedbackDialog open projectId={feedback.projectId} context={feedback.context} onClose={() => setFeedback(undefined)} />}
    </>
  );
}
